import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { agencyService } from '../../services/agencyService';
import { toast } from 'react-toastify';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const AgencySchema = Yup.object().shape({
  name: Yup.string().required('Acente adı gereklidir'),
  email: Yup.string().email('Geçerli bir e-posta adresi giriniz').required('E-posta adresi gereklidir'),
  phone: Yup.string().required('Telefon numarası gereklidir'),
  contactPerson: Yup.string().required('İletişim kişisi gereklidir'),
  address: Yup.string(),
  taxNumber: Yup.string(),
  taxOffice: Yup.string(),
  website: Yup.string().url('Geçerli bir URL giriniz').nullable(),
  isActive: Yup.boolean()
});

const AgencyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchAgency();
  }, [id]);

  const fetchAgency = async () => {
    try {
      const response = await agencyService.getAgencyById(id);
      // API yanıtını kontrol et ve doğru şekilde işle
      if (response && response.agency) {
        setAgency(response.agency);
      } else {
        throw new Error('Acente bilgileri alınamadı');
      }
    } catch (error) {
      toast.error('Acente bilgileri yüklenirken bir hata oluştu');
      navigate('/agencies');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSaving(true);
    try {
      await agencyService.updateAgency(id, values);
      toast.success('Acente başarıyla güncellendi');
      navigate('/agencies');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Acente güncellenirken bir hata oluştu');
      setSubmitting(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Acente bulunamadı</h3>
        <div className="mt-6">
          <Link
            to="/agencies"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Acente Listesine Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Acente Düzenle: {agency.name}</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/agencies"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
            Geri Dön
          </Link>
        </div>
      </div>

      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Acente Bilgileri</h3>
            <p className="mt-1 text-sm text-gray-500">
              Acente bilgilerini güncellemek için formu doldurun.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <Formik
              initialValues={{
                name: agency.name || '',
                email: agency.email || '',
                phone: agency.phone || '',
                contactPerson: agency.contactPerson || '',
                address: agency.address || '',
                taxNumber: agency.taxNumber || '',
                taxOffice: agency.taxOffice || '',
                website: agency.website || '',
                isActive: agency.isActive !== undefined ? agency.isActive : true,
                notes: agency.notes || ''
              }}
              validationSchema={AgencySchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Acente Adı *
                      </label>
                      <Field
                        type="text"
                        name="name"
                        id="name"
                        className={`mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                          errors.name && touched.name ? 'border-red-300' : ''
                        }`}
                      />
                      <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700">
                        İletişim Kişisi *
                      </label>
                      <Field
                        type="text"
                        name="contactPerson"
                        id="contactPerson"
                        className={`mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                          errors.contactPerson && touched.contactPerson ? 'border-red-300' : ''
                        }`}
                      />
                      <ErrorMessage name="contactPerson" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        E-posta Adresi *
                      </label>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        className={`mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                          errors.email && touched.email ? 'border-red-300' : ''
                        }`}
                      />
                      <ErrorMessage name="email" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                        Telefon Numarası *
                      </label>
                      <Field
                        type="text"
                        name="phone"
                        id="phone"
                        className={`mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                          errors.phone && touched.phone ? 'border-red-300' : ''
                        }`}
                      />
                      <ErrorMessage name="phone" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div className="col-span-6">
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                        Adres
                      </label>
                      <Field
                        as="textarea"
                        name="address"
                        id="address"
                        rows={3}
                        className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="taxNumber" className="block text-sm font-medium text-gray-700">
                        Vergi Numarası
                      </label>
                      <Field
                        type="text"
                        name="taxNumber"
                        id="taxNumber"
                        className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="taxOffice" className="block text-sm font-medium text-gray-700">
                        Vergi Dairesi
                      </label>
                      <Field
                        type="text"
                        name="taxOffice"
                        id="taxOffice"
                        className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                        Web Sitesi
                      </label>
                      <Field
                        type="text"
                        name="website"
                        id="website"
                        className={`mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                          errors.website && touched.website ? 'border-red-300' : ''
                        }`}
                        placeholder="https://www.example.com"
                      />
                      <ErrorMessage name="website" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <div className="flex items-center h-full">
                        <Field
                          type="checkbox"
                          name="isActive"
                          id="isActive"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                          Aktif
                        </label>
                      </div>
                    </div>

                    <div className="col-span-6">
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notlar
                      </label>
                      <Field
                        as="textarea"
                        name="notes"
                        id="notes"
                        rows={3}
                        className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Link
                      to="/agencies"
                      className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      İptal
                    </Link>
                    <button
                      type="submit"
                      disabled={isSubmitting || isSaving}
                      className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving || isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Kaydediliyor...
                        </>
                      ) : (
                        'Kaydet'
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyEdit;
